import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { MobileDetection } from '@reverbdotcom/discovery-ui';
import { useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments';

/**
 * Creates a component that displays a series of steps and provides an animation to
 * highlight the current step. Used to show the progress of an item shipment.
 * This ShippingProgressBar can be displayed in mini mode with (min-width: 7em) which
 * displays the current status over the bar. In normal (non-mini) mode
 * the bar displays each step as a label.
 * You will need to supply an i18n prefix for the translation of those labels.
 */
const ShippingProgressBar = (props) => {
  const {
    steps,
    activeStep,
    i18nPrefix,
    mini,
    estimatedDeliveryDateMessage,
    deliveredAtMessage,
    className,
  } = props;

  const buyerTransitUpdatesEnabled = useExpEnabled(experiments.BUYER_TRANSIT_UPDATES);

  // Helper to determine the index of the active step
  const activeIndex = steps.indexOf(activeStep);

  const isDelivered = () => {
    return activeStep === 'delivered';
  };

  const isUnknownStatus = () => {
    return steps.indexOf(activeStep) === -1;
  };

  const renderCurrentStatus = (text) => {
    return (
      <h3 className="progress-bar-header">
        {text}
      </h3>
    );
  };

  const label = (step) => {
    if (mini) { return false; }
    return (
      <div className="label">
        {I18n.t(`${i18nPrefix}.${step}`)}
      </div>
    );
  };

  const path = (index, total) => {
    if (index === total - 1) { return false; }

    const pathClasses = classNames(
      'path',
      { finished: (index <= activeIndex - 1) },
      { active: (index === activeIndex - 1) },
      { warning: (activeIndex === -1) },
    );

    return (
      <li key={`path-${index}`} className={pathClasses} />
    );
  };

  const showCurrentStatus = () => {
    if (!mini && !isDelivered() && estimatedDeliveryDateMessage) {
      return renderCurrentStatus(estimatedDeliveryDateMessage);
    }

    if (!mini && !MobileDetection.isMobileBrowser() && !isUnknownStatus()) {
      return false;
    }

    return renderCurrentStatus(I18n.t(`${i18nPrefix}.${activeStep}`));
  };

  const renderProgressBar = () => {
    return (
      <ul>
        {steps.map((step, index) => {
          const stepClasses = classNames(
            'step',
            { finished: (index <= activeIndex) },
            { current: (index === activeIndex) },
            { warning: (activeIndex === -1) },
          );

          return (
            <React.Fragment key={step}>
              <li className={stepClasses}>{label(step)}</li>
              {path(index, steps.length)}
            </React.Fragment>
          );
        })}
      </ul>
    );
  };

  const classes = classNames(
    'progress-bar',
    className,
    { mini: mini },
  );

  const estimatedDeliverySteps = ['pre_transit', 'in_transit', 'out_for_delivery'];
  const shouldShowEstimatedDelivery = mini && estimatedDeliverySteps.includes(activeStep) && estimatedDeliveryDateMessage;
  const shouldShowDeliveredMessage = mini && isDelivered() && deliveredAtMessage;
  const shouldRenderProgressBar = !shouldShowEstimatedDelivery && !shouldShowDeliveredMessage;

  return (
    <div className={classes}>
      {showCurrentStatus()}
      {buyerTransitUpdatesEnabled ? (
        <React.Fragment>
          {shouldShowEstimatedDelivery && (
            <span key="estimated-delivery" className="estimated-delivery">
              {estimatedDeliveryDateMessage}
            </span>
          )}
          {shouldShowDeliveredMessage && (
            <span key="delivered-at" className="delivered-at">
              {deliveredAtMessage}
            </span>
          )}
          {shouldRenderProgressBar && renderProgressBar()}
        </React.Fragment>
      ) : renderProgressBar()}
    </div>
  );
};


ShippingProgressBar.propTypes = {
  // steps will be displayed in the order they are provided.
  steps: PropTypes.arrayOf(PropTypes.string).isRequired,
  // The active step should be present in the available steps above
  activeStep: PropTypes.string.isRequired,

  i18nPrefix: PropTypes.string,

  /**
  * Use the smaller version of the graph which omits the labels for
  * each step and only shows the current step.
  */
  mini: PropTypes.bool,
  // Estimated delivery date is not always available from the shipping provider. If so pass empty string.
  estimatedDeliveryDateMessage: PropTypes.string.isRequired,
  deliveredAtMessage: PropTypes.string,
  className: PropTypes.string,
};

ShippingProgressBar.defaultProps = {
  mini: false,
  i18nPrefix: 'js.shipping.packageStatus',
};

export default ShippingProgressBar;
